import { Box, SxProps, Theme } from '@mui/material'
import { useId } from 'react'
import markerCar from 'src/images/markerCar.png'
import { layout, vehiclePaint } from 'src/theme/tokens.generated'

// ----------------------------------------------------------------------
// A car seen from above, in the colour that car actually is.
//
// The fleet map drew one of these under every label before the console was
// redesigned, and it was the fastest way to match a pin to the vehicle standing
// in front of you: a person walking into a car park is looking for a white
// estate, not for a legend entry. It went when the marker stopped being a
// bitmap, and it went for a good reason: the four PNGs it was drawn from
// carried the vehicle's *state*, which put the state vocabulary in an image
// directory, so adding a state meant drawing one.
//
// **This is that artwork, and it carries no state.** The render is the app's
// own `marker_car`, which still ships in the mobile bundle and is the same
// top-down Tesla the old console drew; the console's copy of the history
// begins after the redesign, so it was fetched from there rather than lost.
// What decides the colour is one value off the vehicle's own record,
// `exterior`, resolved through `vehiclePaint` in the token source. So the state
// vocabulary stays in the palette where the legend shares it, and the car says
// only what colour it is.
//
// The first attempt at this drew the car as geometry instead, on the argument
// that a silhouette reads better than a photograph at thirty pixels. It does
// not: hand-drawn, it read as a coloured capsule with a dark bar across it, and
// a fleet of them looked like a row of batteries.
//
// **The tint is a multiply, which is why one asset covers twelve paints.** The
// render is a white car, so multiplying it by a paint leaves the paint where
// the body was (white times anything is that thing), keeps the glazing and the
// tyres near black (black times anything is black), and carries the render's
// own shading through as shading rather than flattening it. `feComposite in`
// restores the source alpha afterwards, or the flood would fill the whole box
// and the car would arrive in a coloured square.
//
// **And it points where the car points.** Tesla reports
// `drive_state.heading` as degrees clockwise from true north and the render is
// drawn nose-up, so the mark turns by exactly that with nothing in between to
// round or snap it. It is the same reading the position came from, taken at
// the same moment, which is what makes it safe to draw beside it: a marker
// that turned on a fresher clock than the pin it sits under would be two
// facts wearing one timestamp.
//
// The rotation is on the element rather than inside the `viewBox`, because an
// svg root clips at its viewport: turning the contents would cut the corners
// off the car at every angle that is not a multiple of ninety.
//
// A vehicle with no exterior recorded draws nothing. A grey default would be a
// car this console is asserting the colour of, and `midnightSilver` is a real
// Tesla colour: the guess would be indistinguishable from a reading.
// ----------------------------------------------------------------------

/** The paints a car can be, as the record spells them. */
export type VehiclePaintName = keyof typeof vehiclePaint.paint

const PAINTS = vehiclePaint.paint as Record<string, string>

/**
 * The paint for an exterior, or `null` when there is nothing to draw.
 *
 * `null` rather than a fallback, and that is the whole point: the mark exists
 * to say *this car is that colour*, so a car whose colour is not recorded has
 * to be absent from the sentence rather than given a plausible one.
 *
 * It takes a `string` because the backoffice schema types `Vehicle.exterior`
 * as one. The values are the `VehicleExterior` vocabulary in practice, and the
 * enum is what the edit form writes, but a reader that narrowed to the enum
 * would be asserting a guarantee the wire does not make. An unrecognised value
 * resolves to `null` on the same terms as an absent one.
 */
export const vehiclePaintOf = (
  exterior: string | null | undefined,
): string | null => (exterior ? PAINTS[exterior] ?? null : null)

export interface VehicleTopMarkProps {
  /** The colour off the vehicle record. Nothing is drawn without one. */
  exterior: string | null | undefined
  /**
   * Which way the car is pointing, in degrees clockwise from true north, as
   * Tesla reports it.
   *
   * `null` leaves the car nose-up. That is not the same claim as `0`, which is
   * a reading and means due north, and the difference matters: the schema
   * types the field non-null, so a caller that has no reading at all has to
   * pass `null` rather than letting an absent heading arrive as north.
   */
  heading?: number | null
  /** How wide the car is drawn. Defaults to the map marker's own width. */
  width?: number
  /** What it is, for someone who cannot see it. Absent makes it decorative. */
  title?: string
  sx?: SxProps<Theme>
}

/** The asset is square, and the car is drawn nose-up inside it. */
const VIEW = 54

export default function VehicleTopMark({
  exterior,
  heading,
  width = layout.mapMarkerCar,
  title,
  sx,
}: VehicleTopMarkProps) {
  // One filter per instance. A shared id would give every car on the map the
  // colour of whichever marker rendered last, which is the kind of defect that
  // looks like a caching bug for a week.
  const filterId = useId().replace(/:/g, '')
  const paint = vehiclePaintOf(exterior)

  if (!paint) {
    return null
  }

  return (
    <Box
      component="svg"
      data-testid="vehicle-top-mark"
      data-vehicle-paint={exterior}
      data-vehicle-heading={heading ?? undefined}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      width={width}
      height={width}
      // Decorative unless a caller names it. On the map the marker beside it
      // already carries the vehicle's name and its state, and a second
      // announcement of the same car is noise on the way past.
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      focusable="false"
      sx={[
        {
          display: 'block',
          ...(heading === null || heading === undefined
            ? null
            : { transform: `rotate(${heading}deg)` }),
          // A refreshed reading turns rather than jumps. `enter` and not a
          // literal, and reduced motion drops it to an instant change on its
          // own: the theme's block takes `transform` off the properties a
          // transition may animate, so nothing here needs a second opinion
          // about the preference.
          transition: (theme) => theme.motion.enter.transition('transform'),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {title && <title>{title}</title>}
      <defs>
        {/* `sRGB` and not the SVG default. Filters interpolate in linearRGB
            unless told otherwise, and a multiply done there lightens every
            mid-tone: the same paint came out a different colour from the swatch
            it was read from. */}
        <filter id={filterId} colorInterpolationFilters="sRGB">
          <feFlood floodColor={paint} result="paint" />
          <feBlend
            in="paint"
            in2="SourceGraphic"
            mode="multiply"
            result="body"
          />
          <feComposite in="body" in2="SourceGraphic" operator="in" />
        </filter>
      </defs>
      <image
        href={markerCar}
        x={0}
        y={0}
        width={VIEW}
        height={VIEW}
        filter={`url(#${filterId})`}
      />
    </Box>
  )
}
